import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import ThemeModeProvider from "./theme/ThemeModeProvider";
import BackdropBlur from "./theme/BackdropBlur";
import { AuthProvider, useAuth } from "./common/AuthContext";
import LoginPage from "./pages/LoginPage";
import MainLayout from "./layouts/MainLayout";
import Dashboard from "./pages/Dashboard";
import CardSwipes from "./pages/CardSwipes";
import CardPayments from "./pages/CardPayments";
import Users from "./pages/Users";
import AccessDenied from "./pages/AccessDenied";
import Restricted from "./pages/Restricted";
import StockItems from "./pages/StockItems";
import StockPayments from "./pages/StockPayments";
import CashLedger from "./pages/CashLedger";
import BankTransactions from "./pages/BankTransactions";
import BankAccounts from "./pages/BankAccounts";
import { canReadDashboard } from "./layouts/nav";

function UnauthenticatedPrompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);

  if (location.pathname === "/login") return null;

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
      <Dialog open={open} disableEscapeKeyDown maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Sign-in Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your session has expired or you are not logged in. Please sign in to access the control centre.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setOpen(false);
              navigate("/login", { replace: true });
            }}
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AppRoutes() {
  const { userRole, handleLogin, handleLogout, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isLoggedIn = userRole === "admin" || userRole === "employee" || userRole === "master";
  const seesDashboard = canReadDashboard(userRole);

  /* Admin and Master start on the dashboard; employees, who can't read it,
     start on the swipe register. Used for post-login and catch-all redirects. */
  const landingPath = seesDashboard ? "/dashboard" : "/swipes";

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isLoggedIn
            ? <Navigate to={landingPath} replace />
            : <LoginPage onLogin={handleLogin} />
        }
      />
      <Route path="/access-denied" element={<AccessDenied />} />

      {isLoggedIn ? (
        <Route element={<MainLayout role={userRole} onLogout={handleLogout} />}>
          {/* Visible to every role; Admin and Master see the real thing. */}
          <Route
            path="/dashboard"
            element={seesDashboard ? <Dashboard /> : <Restricted page="The analytics dashboard" requiredRole="Admin or Master" />}
          />

          {/* Card Management pages */}
          <Route path="/swipes" element={<CardSwipes />} />
          <Route path="/transactions" element={<Navigate to="/swipes" replace />} />
          <Route path="/payments"     element={<CardPayments />} />

          {/* Admin + Master: Accounts, Cash & Stock Management */}
          {(userRole === "master" || userRole === "admin") && (
            <>
              <Route path="/bank-transactions" element={<BankTransactions />} />
              <Route path="/bank-accounts"     element={<BankAccounts />} />
              <Route path="/stock-items"   element={<StockItems />} />
              <Route path="/stock-payments" element={<StockPayments />} />
              <Route path="/cash-ledger"   element={<CashLedger />} />
            </>
          )}

          {/* Master only: User Management (previously /parties) */}
          {userRole === "master" && (
            <>
              <Route path="/users"   element={<Users />} />
              {/* Keep /parties alive to avoid hard 404s from old bookmarks */}
              <Route path="/parties" element={<Navigate to="/users" replace />} />
            </>
          )}

          {/* Catch-all → role's landing page */}
          <Route path="*" element={<Navigate to={landingPath} replace />} />
          <Route path="/"  element={<Navigate to={landingPath} replace />} />
        </Route>
      ) : (
        <Route path="*" element={<UnauthenticatedPrompt />} />
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeModeProvider>
      {/* Blurs the page behind dialogs, confirm prompts and the mobile
          drawer. Belongs in theme.js — see the note in BackdropBlur.jsx. */}
      <BackdropBlur>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </BackdropBlur>
    </ThemeModeProvider>
  );
}
